import { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthShell from "./components/AuthShell";

import Shell from "./components/Shell";
import { api, Deposito, Yo } from "./api/client";
import { alcanza } from "./components/Shell";
import ConfigPage from "./pages/Config";
import HistoricoPage from "./pages/Historico";
import MetricsPage from "./pages/Metrics";
import PendingPage from "./pages/Pending";
import ReviewPage from "./pages/Review";
import TrazabilidadPage from "./pages/Trazabilidad";
import UsersPage from "./pages/Users";

const TITULOS: Record<string, [string, string]> = {
  "/pendientes": ["Pendientes", "Depósitos esperando decisión"],
  "/historico": ["Histórico", "Decisiones tomadas"],
  "/metricas": ["Métricas", "Volumen y precisión"],
  "/configuracion": ["Configuración", "Modo de ejecución del agente"],
  "/trazabilidad": ["Trazabilidad", "Qué pasó con un depósito"],
  "/usuarios": ["Usuarios", "Accesos al portal"],
};

function App({ signOut, user }: { signOut?: () => void; user?: any }) {
  const { pathname } = useLocation();
  const [cola, setCola] = useState<Deposito[]>([]);
  const [cargando, setCargando] = useState(true);
  const [falla, setFalla] = useState<string | null>(null);
  // Politica de la cola: solo se valida lo que ARCA confirmo. Viene del
  // backend para poder cambiarla desde Configuracion sin redesplegar.
  const [exigirArca, setExigirArca] = useState(true);
  const [yo, setYo] = useState<Yo | null>(null);
  // Ventana de la cola. Por defecto la semana: un depósito de hace tres
  // semanas sin decidir es una excepción que se busca a propósito, no algo
  // que deba estorbar la cola de todos los días.
  const [rango, setRango] = useState("semana");
  const [dia, setDia] = useState("");
  /** Rango a medida elegido en el calendario. Ambos o ninguno. */
  const [aMedida, setAMedida] = useState<[string, string] | null>(null);
  const primeraCarga = useRef(true);

  // Quién soy y qué puedo ver. Si falla, se asume el rol más bajo: mejor
  // esconder de más que ofrecer una puerta que va a devolver 403.
  useEffect(() => {
    api.getMe().then(setYo).catch(() => setYo(null));
  }, []);

  // La cola vive acá porque la comparten Pendientes y Revisión: la pantalla de
  // revisión necesita saber cuál es el siguiente para navegar sin volver.
  const cargar = useCallback(() => {
    // "Cargando" solo la primera vez. Al cambiar de rango o refrescar, la
    // cola anterior se queda en pantalla mientras llega la nueva: vaciarla
    // para volver a llenarla es un parpadeo que no aporta nada.
    if (primeraCarga.current) setCargando(true);
    api.getPending(rango, dia, aMedida?.[0], aMedida?.[1])
      .then((r) => {
        setCola(r.items ?? []);
        setExigirArca(r.exigir_arca !== false);
        setFalla(null);
      })
      // Una falla NO es una cola vacia. Devolver [] hacia que un backend caido
      // se viera identico a "no hay nada que validar", y en una mesa que
      // aprueba desembolsos ese es el peor modo de falla: se disfraza de calma.
      // Paso de verdad — el preflight de CORS volvia 401 y el portal mostraba
      // "No hay depositos" con 309 esperando.
      .catch((e) => setFalla(e?.message || "No se pudo cargar la cola."))
      .finally(() => { setCargando(false); primeraCarga.current = false; });
  }, [rango, dia, aMedida]);

  useEffect(cargar, [cargar]);

  const base = "/" + (pathname.split("/")[1] || "pendientes");
  const [titulo, subtitulo] = TITULOS[base] ?? ["Validación de vouchers", ""];
  const enRevision = pathname.startsWith("/pendientes/");

  return (
    <Shell
      titulo={enRevision ? "Revisión de voucher" : titulo}
      subtitulo={enRevision ? "Aprueba o rechaza y sigue con el siguiente" : subtitulo}
      email={user?.signInDetails?.loginId}
      onSignOut={signOut}
      pendientes={cola.length}
      rol={yo?.rol}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/pendientes" replace />} />
        <Route path="/pendientes" element={
          <PendingPage items={cola} cargando={cargando} falla={falla}
                       exigirArca={exigirArca} onRefrescar={cargar}
                       rango={rango} dia={dia} aMedida={aMedida}
                       onRango={(r, d, m) => {
                         setRango(r); setDia(d ?? ""); setAMedida(m ?? null);
                       }} />} />
        <Route path="/pendientes/:id" element={
          <ReviewPage cola={cola} onResuelto={cargar} />} />
        <Route path="/historico" element={<HistoricoPage />} />
        <Route path="/metricas" element={<MetricsPage />} />
        {/* `!yo` = todavía no sabemos el rol (o el backend no expone
            /api/me). Se deja pasar y manda el 403 del API, que es la barrera
            de verdad. Bloquear sin saber sería inventar un permiso. */}
        <Route path="/configuracion" element={
          !yo || alcanza(yo.rol, "admin") ? <ConfigPage /> : <SinAcceso />} />
        <Route path="/trazabilidad" element={
          !yo || alcanza(yo.rol, "admin") ? <TrazabilidadPage /> : <SinAcceso />} />
        <Route path="/usuarios" element={
          !yo || alcanza(yo.rol, "admin") ? <UsersPage yo={yo} /> : <SinAcceso />} />
        <Route path="*" element={<Navigate to="/pendientes" replace />} />
      </Routes>
    </Shell>
  );
}

/**
 * Alguien escribió la dirección de un módulo que su rol no alcanza. Se le
 * dice, en vez de mostrarle una pantalla rota o un error del API.
 */
function SinAcceso() {
  return (
    <div className="empty falla">
      <div className="falla-titulo">Esta sección no está disponible para tu rol</div>
      <div className="falla-nota">
        Si necesitas entrar, pídele acceso a un administrador del portal.
      </div>
    </div>
  );
}

/**
 * El login envuelve a la app: el layout de dos paneles del diseño solo se ve
 * cuando no hay sesión (AuthAside se oculta solo al autenticarse).
 */
export default function Root() {
  return (
    <AuthShell>{({ signOut, user }) => <App signOut={signOut} user={user} />}</AuthShell>
  );
}
